import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Header from '../../components/Header';
import CrisisResourceBanner from '../../components/CrisisResourceBanner';
import SessionStatusIndicator from '../../components/SessionStatusIndicator';
import Button from '../../components/ui/Button';
import CalendarView from './components/CalendarView';
import BookingForm from './components/BookingForm';
import HelperCard from './components/HelperCard';
import ConfirmationModal from './components/ConfirmationModal';
import BookingInfoCards from './components/BookingInfoCards';
import { useHelperBooking } from './hooks/useHelperBooking';
import { appointmentService } from '../../services/appointmentService';
import { emailService } from '../../services/emailService';
import { useStripeContext } from '../../contexts/StripeContext';
import Icon from '../../components/AppIcon';

const BookingScheduling = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [pendingBookingDetails, setPendingBookingDetails] = useState(null);

  const { selectedHelper, loading, error } = useHelperBooking(location);
  const { stripePromise, stripeOptions } = useStripeContext();

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleClearSelection = () => {
    setSelectedSlot(null);
  };

  const handleConfirmBooking = async (bookingDetails) => {
    setBookingInProgress(true);
    
    try {
      // Check if helper has booking charge enabled
      if (selectedHelper?.bookingChargeEnabled && selectedHelper?.bookingChargeAmount) {
        // Process payment first
        const { data: paymentData, error: paymentError } = await appointmentService?.processBookingPayment(
          selectedHelper?.id,
          null, // appointmentId will be null until after payment
          selectedHelper?.bookingChargeAmount,
          selectedHelper?.bookingChargeCurrency || 'USD'
        );

        if (paymentError) {
          alert(`Payment initialization failed: ${paymentError?.message}`);
          setBookingInProgress(false);
          return;
        }

        // Store booking details and show payment modal
        setPendingBookingDetails(bookingDetails);
        setPaymentClientSecret(paymentData?.clientSecret);
        setPaymentIntentId(paymentData?.paymentIntentId);
        setShowPaymentModal(true);
        setBookingInProgress(false);
      } else {
        // No payment required, create appointment directly
        await createAppointment(bookingDetails, null);
      }
    } catch (err) {
      alert(`Booking failed: ${err?.message}`);
      setBookingInProgress(false);
    }
  };

  const createAppointment = async (bookingDetails, stripePaymentIntentId) => {
    try {
      const appointmentData = {
        helperId: selectedHelper?.id,
        sessionType: selectedSlot?.type || 'video',
        appointmentDate: selectedSlot?.date,
        appointmentTime: selectedSlot?.time,
        duration: selectedSlot?.duration || 60,
        topic: bookingDetails?.topic || 'General Session',
        location: bookingDetails?.location || selectedHelper?.location,
        preparationNotes: bookingDetails?.notes || '',
        bookingChargeAmount: selectedHelper?.bookingChargeAmount || null,
        bookingChargeCurrency: selectedHelper?.bookingChargeCurrency || null,
        stripePaymentIntentId: stripePaymentIntentId
      };
      
      const { data, error: bookingError } = await appointmentService?.createAppointment(appointmentData);
      
      if (bookingError) {
        alert(`Booking failed: ${bookingError?.message}`);
        return;
      }
      
      emailService?.sendAppointmentConfirmation(appointmentData, selectedHelper);
      
      // Add booking charge info to slot for confirmation modal
      const updatedSlot = {
        ...selectedSlot,
        bookingChargeAmount: selectedHelper?.bookingChargeAmount,
        bookingChargeCurrency: selectedHelper?.bookingChargeCurrency
      };
      setSelectedSlot(updatedSlot);
      
      setShowPaymentModal(false);
      setIsConfirmationOpen(true);
    } catch (err) {
      alert(`Booking failed: ${err?.message}`);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    // Create appointment with payment intent ID
    await createAppointment(pendingBookingDetails, paymentIntent?.id);
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setPaymentClientSecret(null);
    setPaymentIntentId(null);
    setPendingBookingDetails(null);
  };

  const handleBackToDiscovery = () => {
    navigate('/helper-discovery');
  };

  const handleChangeHelper = () => {
    navigate('/helper-discovery');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading booking information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-error mb-4">{error}</p>
            <button 
              onClick={() => navigate('/helper-discovery')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Browse Helpers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-32">
      <CrisisResourceBanner />
      <Header />
      <SessionStatusIndicator sessionActive={false} />
      <div className="pt-24 pb-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <Button
              variant="ghost"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleBackToDiscovery}
            >
              Back to Helper Discovery
            </Button>
          </div>

          <div className="mb-6">
            <h1 className="text-3xl font-semibold text-foreground mb-2">Schedule Your Session</h1>
            <p className="text-muted-foreground">
              Select an available time slot and configure your session preferences
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <CalendarView
                selectedHelper={selectedHelper}
                onSlotSelect={handleSlotSelect}
                selectedSlot={selectedSlot}
              />
            </div>

            <div className="space-y-6">
              <HelperCard
                helper={selectedHelper}
                onChangeHelper={handleChangeHelper}
              />

              <BookingForm
                selectedSlot={selectedSlot}
                selectedHelper={selectedHelper}
                onConfirm={handleConfirmBooking}
                onCancel={handleClearSelection}
              />
            </div>
          </div>

          <BookingInfoCards />
        </div>
      </div>
      {showPaymentModal && paymentClientSecret && (
        <div className="fixed inset-0 z-[900] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-lg border border-border shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Complete Payment</h2>
                <button
                  onClick={handlePaymentCancel}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="mb-6 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Booking Charge</span>
                  <span className="text-lg font-bold text-foreground">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: selectedHelper?.bookingChargeCurrency || 'USD'
                    })?.format(selectedHelper?.bookingChargeAmount)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Session with {selectedHelper?.name}
                </p>
              </div>

              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: paymentClientSecret,
                  ...stripeOptions
                }}
              >
                <PaymentForm
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}
      {isConfirmationOpen && selectedSlot && (
        <ConfirmationModal
          helper={selectedHelper}
          slot={selectedSlot}
          onClose={() => setIsConfirmationOpen(false)}
          onConfirm={() => {}}
          isSubmitting={false}
          onViewDashboard={() => navigate('/personal-dashboard')}
        />
      )}
    </div>
  );
};

// Payment form component using Stripe Elements
const PaymentForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage('');

    try {
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        redirect: 'if_required'
      });

      if (stripeError) {
        setErrorMessage(stripeError?.message || 'Payment failed');
        setProcessing(false);
      } else if (paymentIntent && paymentIntent?.status === 'succeeded') {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      setErrorMessage(err?.message || 'An error occurred');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />

      {errorMessage && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-sm text-error">{errorMessage}</p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          fullWidth
          disabled={processing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          iconName={processing ? "Loader2" : "CreditCard"}
          iconClassName={processing ? "animate-spin" : ""}
          fullWidth
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : 'Pay Now'}
        </Button>
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Icon name="Shield" size={12} />
        <span>Secured by Stripe</span>
      </div>
    </form>
  );
};

export default BookingScheduling;